import React, { useState, useEffect } from 'react';
import StatsCardsDashboard from '../../../components/shared/StatsCardsDashboard';
import budgetService from '../services/budgetService';
import {
  AccountBalance,
  ShoppingCart,
  TrendingDown,
  Savings
} from '@mui/icons-material';

/**
 * BudgetStatsDashboard Component
 * 
 * Displays key budget statistics at the top of the budgeting section
 */
const BudgetStatsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [budgetStats, setBudgetStats] = useState({
    totalBudgeted: 0,
    spent: 0,
    remaining: 0,
    savingsRate: 0,
    budgetCount: 0,
    categoryCoverage: 0,
    utilization: 0
  });

  useEffect(() => {
    const fetchBudgetStats = async () => {
      try {
        setLoading(true);
        console.log('🔄 Fetching budget statistics...');
        
        // Try to get stats directly from API first
        try {
          const stats = await budgetService.getBudgetStatistics();
          console.log('✅ Budget statistics received:', stats);
          setBudgetStats(stats);
          setLoading(false);
          return; // Exit early if successful
        } catch (statsError) {
          console.error('❌ Error fetching budget statistics from stats endpoint:', statsError);
          console.log('🔄 Falling back to manual calculation...');
          // Continue to fallback
        }

        // Start debug message for fallback logic
        console.log('🔍 Starting fallback logic to calculate stats manually');
        
        // Direct fallback logic - don't wait for service fallback
        let budgets;
        try {
          console.log('🔄 Fetching all budgets for manual calculation...');
          budgets = await budgetService.getAllBudgets();
          console.log('✅ Successfully retrieved budgets:', budgets);
        } catch (budgetsError) {
          console.error('❌ Error fetching budgets for calculation:', budgetsError);
          console.log('⚠️ Using empty budget array for calculations');
          budgets = [];
        }
        
        if (!budgets || !Array.isArray(budgets) || budgets.length === 0) {
          console.log('ℹ️ No budgets found or invalid data, using zero values');
          setBudgetStats({
            totalBudgeted: 0,
            spent: 0,
            remaining: 0,
            savingsRate: 0,
            budgetCount: 0,
            categoryCoverage: 0,
            utilization: 0
          });
          setLoading(false);
          return;
        }
        
        // Find active budget
        const activeBudget = budgets.find(b => b.isPrimary || b.isActive) || budgets[0];
        console.log('📊 Using budget for calculation:', activeBudget);
        
        // Safely access categories with fallbacks for every step
        const categories = activeBudget && activeBudget.categories ? 
          (Array.isArray(activeBudget.categories) ? activeBudget.categories : []) 
          : [];
        
        console.log('📊 Found categories:', categories);
        
        // Safely calculate stats with defensive coding
        const totalBudgeted = categories.reduce((sum, cat) => {
          const budgeted = cat && cat.budgeted ? parseFloat(cat.budgeted) : 0;
          return sum + (isNaN(budgeted) ? 0 : budgeted);
        }, 0);
        
        const spent = categories.reduce((sum, cat) => {
          const actual = cat && cat.actual ? parseFloat(cat.actual) : 0;
          return sum + (isNaN(actual) ? 0 : actual);
        }, 0);
        
        console.log('📊 Calculated budget amounts - Total:', totalBudgeted, 'Spent:', spent);
        
        const remaining = totalBudgeted - spent;
        
        // Find savings categories
        const savingsCategories = categories.filter(cat => 
          cat && cat.name && typeof cat.name === 'string' && cat.name.toLowerCase().includes('saving')
        );
        
        const savingsAmount = savingsCategories.reduce((sum, cat) => {
          const budgeted = cat && cat.budgeted ? parseFloat(cat.budgeted) : 0;
          return sum + (isNaN(budgeted) ? 0 : budgeted);
        }, 0);
        
        // Calculate income
        const incomeData = activeBudget && activeBudget.income ? 
          (Array.isArray(activeBudget.income) ? activeBudget.income : []) 
          : [];
          
        const incomeTotal = incomeData.reduce((sum, inc) => {
          const amount = inc && inc.amount ? parseFloat(inc.amount) : 0;
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);
        
        const savingsRate = incomeTotal > 0 ? (savingsAmount / incomeTotal) * 100 : 0;
        
        console.log('📊 Income and savings - Income:', incomeTotal, 'Savings:', savingsAmount, 'Rate:', savingsRate);
        
        const calculatedStats = {
          totalBudgeted,
          spent,
          remaining,
          savingsRate,
          budgetCount: budgets.length,
          categoryCoverage: categories.length > 0 
            ? (categories.filter(c => (c && c.budgeted && parseFloat(c.budgeted) > 0)).length / categories.length) * 100 
            : 0,
          utilization: totalBudgeted > 0 ? (spent / totalBudgeted) * 100 : 0
        };
        
        console.log('✅ Final calculated budget statistics:', calculatedStats);
        setBudgetStats(calculatedStats);
      } catch (error) {
        console.error('❌ All attempts to fetch budget statistics failed:', error);
        console.log('⚠️ Using default zero values as last resort');
        // Keep using the default state values as fallback
      } finally {
        setLoading(false);
      }
    };

    fetchBudgetStats();
  }, []);

  const formatCurrency = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '$0.00';
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '0.0%';
    }
    return `${value.toFixed(1)}%`;
  };

  const budgetMetrics = [
    {
      title: 'Total Budgeted',
      value: formatCurrency(budgetStats.totalBudgeted),
      description: 'Monthly allocation',
      icon: AccountBalance,
      color: 'indigo'
    },
    {
      title: 'Spent So Far',
      value: formatCurrency(budgetStats.spent),
      description: `${formatPercentage(budgetStats.utilization || 0)} of budget used`,
      change: formatPercentage(budgetStats.utilization || 0),
      isPositive: budgetStats.utilization <= 100,
      icon: ShoppingCart,
      color: 'orange'
    },
    {
      title: 'Remaining',
      value: formatCurrency(budgetStats.remaining),
      description: 'Available to spend',
      icon: TrendingDown,
      color: 'green'
    },
    {
      title: 'Savings Rate',
      value: formatPercentage(budgetStats.savingsRate),
      description: 'Of total income',
      change: `+2.3% from last month`,
      isPositive: true,
      icon: Savings,
      color: 'purple'
    }
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6">
      <StatsCardsDashboard
        title="Budget"
        metrics={budgetMetrics}
        loading={loading}
      />
    </div>
  );
};

export default BudgetStatsDashboard; 